import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { formatMoney } from "@/lib/constants";
import { toast } from "sonner";
import { AdminSeo } from "@/components/AdminSeo";
import { Users, Megaphone, Wallet, Search, LogOut, Activity, MousePointerClick, LogIn, Eye, Zap, UserPlus, ShieldAlert, RefreshCw } from "lucide-react";

const KIND_ICON = { login: LogIn, click: MousePointerClick, page_view: Eye, api: Zap, admin: Activity, signup: UserPlus, login_failed: ShieldAlert, logout: LogOut };
const KIND_FILTERS = [["", "All"], ["signup", "Sign-ups"], ["login", "Sign-ins"], ["login_failed", "Failed sign-ins"], ["logout", "Sign-outs"], ["api", "Actions"], ["page_view", "Page views"], ["click", "Clicks"]];
const when = (s) => (s ? new Date(s).toLocaleString() : "—");

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="font-heading font-black text-2xl mt-1">{value ?? "—"}</p>
    </div>
  );
}

function ActivityFeed({ items, testid }) {
  if (!items) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!items.length) return <p className="text-sm text-muted-foreground" data-testid={`${testid}-empty`}>No activity yet.</p>;
  return (
    <div className="divide-y divide-border rounded-2xl border border-border bg-surface" data-testid={testid}>
      {items.map((e) => {
        const Icon = KIND_ICON[e.kind] || Activity;
        return (
          <div key={e.event_id} className="flex items-start gap-3 p-3 text-sm">
            <Icon className="h-4 w-4 mt-0.5 text-brand-pink shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-medium break-words">{e.action}{e.role && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-border text-muted-foreground">{e.role}</span>}</p>
              <p className="text-xs text-muted-foreground truncate">{e.name || e.email} · {when(e.created_at)}{e.path ? ` · ${e.path}` : ""}{e.status ? ` · ${e.status}` : ""}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function UserActivitySheet({ u, onClose }) {
  const [items, setItems] = useState(null);
  const [kind, setKind] = useState("");
  useEffect(() => {
    if (!u) { return; }
    setItems(null);
    api.get("/admin/activity", { params: { user_id: u.user_id, kind: kind || undefined, limit: 300 } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [u, kind]);
  return (
    <Sheet open={!!u} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto bg-background" data-testid="user-activity-sheet">
        <SheetHeader><SheetTitle className="font-heading text-2xl">{u?.name}</SheetTitle></SheetHeader>
        <p className="text-sm text-muted-foreground mb-4">{u?.email}</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {[["", "All"], ["signup", "Sign-ups"], ["login", "Logins"], ["api", "Actions"], ["page_view", "Page views"], ["click", "Clicks"]].map(([k, l]) => (
            <button key={l} onClick={() => setKind(k)} data-testid={`activity-filter-${l.toLowerCase().replace(" ", "-")}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold border ${kind === k ? "bg-brand-pink text-white border-brand-pink" : "border-border"}`}>{l}</button>
          ))}
        </div>
        <ActivityFeed items={items} testid="user-activity-feed" />
      </SheetContent>
    </Sheet>
  );
}

function UsersTab({ role }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [feed, setFeed] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/users", { params: { role, q: q || undefined } }).then((r) => setRows(r.data)).catch(() => setRows([])), 300);
    return () => clearTimeout(t);
  }, [role, q]);
  useEffect(() => {
    api.get("/admin/activity", { params: { role, limit: 60 } }).then((r) => setFeed(r.data)).catch(() => setFeed([]));
  }, [role]);
  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 min-w-0">
        <div className="relative mb-4"><Search className="h-4 w-4 absolute left-3 top-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company…" data-testid={`admin-search-${role}`} className="pl-9 h-11 bg-surface border-border" /></div>
        <div className="rounded-2xl border border-border bg-surface overflow-x-auto">
          <table className="w-full text-sm" data-testid={`admin-users-table-${role}`}>
            <thead className="text-xs text-muted-foreground uppercase tracking-wider"><tr className="border-b border-border">
              <th className="text-left p-3">User</th><th className="text-left p-3">{role === "promoter" ? "Campaigns" : "Bids"}</th><th className="text-left p-3">Logins</th><th className="text-left p-3">Events</th><th className="text-left p-3">Last active</th>
            </tr></thead>
            <tbody>
              {(rows || []).map((r) => (
                <tr key={r.user_id} onClick={() => setSel(r)} data-testid={`admin-user-row-${r.user_id}`} className="border-b border-border last:border-0 hover:bg-surfaceHover cursor-pointer">
                  <td className="p-3"><div className="flex items-center gap-2 min-w-[180px]"><img src={r.picture} alt="" className="h-8 w-8 rounded-full object-cover" /><div className="min-w-0"><p className="font-semibold truncate">{r.company_name || r.name}</p><p className="text-xs text-muted-foreground truncate">{r.email}</p></div></div></td>
                  <td className="p-3">{role === "promoter" ? r.campaigns : r.bids}</td>
                  <td className="p-3">{r.logins}</td>
                  <td className="p-3">{r.events}</td>
                  <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{when(r.last_active)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows && !rows.length && <p className="p-6 text-sm text-muted-foreground">No users found.</p>}
          {!rows && <p className="p-6 text-sm text-muted-foreground">Loading…</p>}
        </div>
      </div>
      <div className="lg:col-span-2 min-w-0">
        <h3 className="font-heading font-bold mb-3">Recent {role} activity</h3>
        <ActivityFeed items={feed} testid={`admin-feed-${role}`} />
      </div>
      <UserActivitySheet u={sel} onClose={() => setSel(null)} />
    </div>
  );
}

function WithdrawalsTab({ onChange }) {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("requested");
  const load = () => api.get("/admin/withdrawals", { params: { status: status || undefined } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (w, action) => {
    const reference = action === "mark-paid" ? window.prompt("Payment reference (UTR / transaction id):", "") : null;
    if (action === "mark-paid" && reference === null) { return; }
    const note = action === "reject" ? window.prompt("Reason for rejection (shown in creator's history):", "") : null;
    if (action === "reject" && note === null) { return; }
    try {
      await api.post(`/admin/withdrawals/${w.withdrawal_id}/${action}`, { reference, note });
      toast.success(action === "mark-paid" ? "Marked as paid." : "Rejected — amount returned to creator's wallet.");
      load(); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        {[["requested", "Pending"], ["paid", "Paid"], ["rejected", "Rejected"], ["", "All"]].map(([k, l]) => (
          <button key={l} onClick={() => setStatus(k)} data-testid={`withdrawal-filter-${l.toLowerCase()}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${status === k ? "bg-brand-pink text-white border-brand-pink" : "border-border"}`}>{l}</button>
        ))}
      </div>
      {!rows ? <p className="text-sm text-muted-foreground">Loading…</p> : !rows.length ? <p className="text-sm text-muted-foreground" data-testid="withdrawals-empty">No withdrawal requests.</p> : (
        <div className="space-y-3" data-testid="admin-withdrawals-list">
          {rows.map((w) => (
            <div key={w.withdrawal_id} className="rounded-2xl border border-border bg-surface p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`withdrawal-row-${w.withdrawal_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-black text-xl">{formatMoney(w.amount, w.currency)}</p>
                <p className="text-sm">{w.user_name} · <span className="text-muted-foreground">{w.user_email}</span></p>
                <p className="text-sm break-words"><span className="text-muted-foreground">Pay to: </span>{w.payout_details}</p>
                <p className="text-xs text-muted-foreground">Requested {when(w.created_at)} · <span className="capitalize font-semibold">{w.status}</span>{w.reference ? ` · Ref ${w.reference}` : ""}{w.admin_note ? ` · ${w.admin_note}` : ""}</p>
              </div>
              {w.status === "requested" && (
                <div className="flex gap-2">
                  <Button onClick={() => act(w, "mark-paid")} data-testid={`mark-paid-${w.withdrawal_id}`} className="rounded-full bg-neon-lime text-background font-semibold">Mark as paid</Button>
                  <Button variant="outline" onClick={() => act(w, "reject")} data-testid={`reject-withdrawal-${w.withdrawal_id}`} className="rounded-full">Reject</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityTab() {
  const [kind, setKind] = useState("");
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [more, setMore] = useState(true);
  const params = (before) => ({ kind: kind || undefined, role: role || undefined, q: q || undefined, before, limit: 100 });
  const load = () => {
    api.get("/admin/activity", { params: params() }).then((r) => { setItems(r.data); setMore(r.data.length === 100); }).catch(() => setItems([]));
    api.get("/admin/activity/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => {
    const t = setTimeout(load, 300);
    const iv = setInterval(load, 30000);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, [kind, role, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadMore = () => {
    const last = items?.[items.length - 1];
    if (!last) { return; }
    api.get("/admin/activity", { params: params(last.created_at) }).then((r) => { setItems((s) => [...s, ...r.data]); setMore(r.data.length === 100); });
  };
  return (
    <div data-testid="admin-activity-tab">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
        <Stat label="Sign-ups · 24h" value={stats?.signup} testid="activity-stat-signups" />
        <Stat label="Sign-ins · 24h" value={stats?.login} testid="activity-stat-logins" />
        <Stat label="Failed sign-ins" value={stats?.login_failed} testid="activity-stat-failed" />
        <Stat label="Actions · 24h" value={stats?.api} testid="activity-stat-actions" />
        <Stat label="Clicks · 24h" value={stats?.click} testid="activity-stat-clicks" />
        <Stat label="Active users · 24h" value={stats?.active_users} testid="activity-stat-active" />
      </div>
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email, name or action…" data-testid="activity-search" className="pl-9 h-11 bg-surface border-border" /></div>
        <div className="flex gap-2">
          {[["", "All users"], ["influencer", "Influencers"], ["promoter", "Promoters"]].map(([k, l]) => (
            <button key={l} onClick={() => setRole(k)} data-testid={`activity-role-${k || "all"}`}
              className={`px-3 h-11 rounded-full text-xs font-semibold border ${role === k ? "bg-foreground text-background border-foreground" : "border-border"}`}>{l}</button>
          ))}
          <Button variant="outline" onClick={load} data-testid="activity-refresh-btn" className="rounded-full h-11"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {KIND_FILTERS.map(([k, l]) => (
          <button key={l} onClick={() => setKind(k)} data-testid={`activity-kind-${k || "all"}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${kind === k ? "bg-brand-pink text-white border-brand-pink" : "border-border"}`}>{l}</button>
        ))}
      </div>
      <ActivityFeed items={items} testid="admin-activity-feed" />
      {more && items?.length > 0 && <Button variant="outline" onClick={loadMore} data-testid="activity-load-more" className="mt-4 rounded-full w-full">Load more</Button>}
    </div>
  );
}

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const [sum, setSum] = useState(null);
  const loadSum = () => api.get("/admin/summary").then((r) => setSum(r.data)).catch(() => {});
  useEffect(() => { if (user?.is_admin) { loadSum(); } }, [user]);
  if (!user?.is_admin) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen bg-background overflow-x-hidden" data-testid="admin-panel">
      <header className="border-b border-border px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3"><Logo size="sm" to="/admin" /><span className="text-xs font-bold uppercase tracking-widest text-brand-pink">Admin</span></div>
        <div className="flex items-center gap-3 text-sm"><span className="text-muted-foreground hidden sm:inline">{user.email}</span>
          <Button variant="outline" onClick={logout} data-testid="admin-logout-btn" className="rounded-full h-9"><LogOut className="h-4 w-4 mr-1" /> Log out</Button></div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading font-black text-4xl tracking-tight mb-6">Admin panel</h1>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          <Stat label="Influencers" value={sum?.influencers} testid="admin-stat-influencers" />
          <Stat label="Promoters" value={sum?.promoters} testid="admin-stat-promoters" />
          <Stat label="Campaigns" value={sum?.campaigns} testid="admin-stat-campaigns" />
          <Stat label="Bids" value={sum?.bids} testid="admin-stat-bids" />
          <Stat label="In escrow" value={sum?.escrow_funded} testid="admin-stat-escrow" />
          <Stat label="Pending payouts" value={sum?.pending_withdrawals} testid="admin-stat-withdrawals" />
        </div>
        <Tabs defaultValue="activity">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="activity" data-testid="admin-tab-activity"><Activity className="h-4 w-4 mr-1" /> Activity</TabsTrigger>
            <TabsTrigger value="influencer" data-testid="admin-tab-influencers"><Users className="h-4 w-4 mr-1" /> Influencers</TabsTrigger>
            <TabsTrigger value="promoter" data-testid="admin-tab-promoters"><Megaphone className="h-4 w-4 mr-1" /> Promoters</TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="admin-tab-withdrawals"><Wallet className="h-4 w-4 mr-1" /> Withdrawals</TabsTrigger>
            <TabsTrigger value="seo" data-testid="admin-tab-seo"><Search className="h-4 w-4 mr-1" /> SEO pages</TabsTrigger>
          </TabsList>
          <TabsContent value="activity"><ActivityTab /></TabsContent>
          <TabsContent value="influencer"><UsersTab role="influencer" /></TabsContent>
          <TabsContent value="promoter"><UsersTab role="promoter" /></TabsContent>
          <TabsContent value="withdrawals"><WithdrawalsTab onChange={loadSum} /></TabsContent>
          <TabsContent value="seo"><AdminSeo /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
